import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { Download, X } from "@/icons/lucide-compat";
import { ToolcraftButton as Button, ToolcraftText as Text } from "@openreel/ui";

/**
 * The install event is Chromium-only. Safari and Firefox never fire it, which
 * is exactly why the affordance is driven by the event rather than by sniffing
 * the platform: on macOS Safari the user reaches File > Add to Dock instead,
 * and this component stays invisible.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface Window {
    /** Stashed by the inline snippet in index.html before React mounts. */
    __flshyInstallPrompt?: BeforeInstallPromptEvent | null;
  }
}

export const INSTALL_DISMISSED_KEY = "flshy.install-prompt.dismissed";

/** Fired by index.html once it has stashed a `beforeinstallprompt` event. */
export const INSTALL_AVAILABLE_EVENT = "flshy:installavailable";

export function shouldOfferInstall(state: {
  hasPromptEvent: boolean;
  isDismissed: boolean;
  isStandalone: boolean;
}): boolean {
  return state.hasPromptEvent && !state.isDismissed && !state.isStandalone;
}

export function readDismissed(storage: Storage | undefined): boolean {
  try {
    return storage?.getItem(INSTALL_DISMISSED_KEY) === "1";
  } catch {
    // Safari private browsing throws on storage access; offering the prompt
    // is the harmless side of that failure.
    return false;
  }
}

export function writeDismissed(storage: Storage | undefined): void {
  try {
    storage?.setItem(INSTALL_DISMISSED_KEY, "1");
  } catch {
    // Nothing to recover: the prompt reappears next session at worst.
  }
}

/** True when this document is already the installed app rather than a tab. */
export function isStandaloneDisplay(win: Window): boolean {
  const navigatorWithStandalone = win.navigator as Navigator & {
    standalone?: boolean;
  };
  if (navigatorWithStandalone.standalone === true) {
    return true;
  }
  if (typeof win.matchMedia !== "function") {
    return false;
  }
  return (
    win.matchMedia("(display-mode: standalone)").matches ||
    win.matchMedia("(display-mode: window-controls-overlay)").matches
  );
}

export function InstallPrompt(): JSX.Element | null {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(
      () => window.__flshyInstallPrompt ?? null,
    );
  const [isDismissed, setIsDismissed] = useState(() =>
    readDismissed(window.localStorage),
  );
  // A document's display mode is fixed for its lifetime, so this is read once
  // rather than on every render of the app shell.
  const [isStandalone] = useState(() => isStandaloneDisplay(window));

  useEffect(() => {
    const adopt = (): void => {
      setPromptEvent(window.__flshyInstallPrompt ?? null);
    };

    // Also listened for directly so the component still works if the index.html
    // snippet is ever dropped: it only exists to win the pre-mount race.
    const capture = (event: Event): void => {
      event.preventDefault();
      window.__flshyInstallPrompt = event as BeforeInstallPromptEvent;
      setPromptEvent(event as BeforeInstallPromptEvent);
    };

    const forget = (): void => {
      window.__flshyInstallPrompt = null;
      setPromptEvent(null);
      writeDismissed(window.localStorage);
      setIsDismissed(true);
    };

    window.addEventListener(INSTALL_AVAILABLE_EVENT, adopt);
    window.addEventListener("beforeinstallprompt", capture);
    window.addEventListener("appinstalled", forget);

    return () => {
      window.removeEventListener(INSTALL_AVAILABLE_EVENT, adopt);
      window.removeEventListener("beforeinstallprompt", capture);
      window.removeEventListener("appinstalled", forget);
    };
  }, []);

  const dismiss = useCallback(() => {
    writeDismissed(window.localStorage);
    setIsDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) {
      return;
    }

    // A prompt event can only be used once, so it is discarded whatever
    // happens, including a Chrome that rejects it for a stale gesture.
    window.__flshyInstallPrompt = null;
    setPromptEvent(null);

    try {
      await promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      // Declining counts as a "no" we remember; re-offering on every visit is
      // the nagging we want to avoid.
      if (outcome === "dismissed") {
        dismiss();
      }
    } catch (error) {
      console.warn("[InstallPrompt] install prompt failed:", error);
    }
  }, [promptEvent, dismiss]);

  const visible = shouldOfferInstall({
    hasPromptEvent: promptEvent !== null,
    isDismissed,
    isStandalone,
  });

  if (!visible) {
    return null;
  }

  // Entrance is a CSS animation rather than an animated mount/unmount: an exit
  // transition driven by requestAnimationFrame never finishes in a hidden tab,
  // which leaves a dismissed pill in the DOM at opacity 0 still swallowing
  // clicks in the corner.
  return (
    <div className="fixed bottom-4 right-4 z-[9990] flex items-center gap-2 rounded-xl border border-border bg-bg-1 py-2 pl-3 pr-2 shadow-md animate-in fade-in slide-in-from-bottom-2 duration-200">
      <Download size={15} className="shrink-0 text-accent" />
      <Text type="supporting" color="secondary" className="text-xs">
        Install flshy for offline editing
      </Text>
      <Button size="sm" variant="primary" label="Install" onClick={install} />
      <button
        type="button"
        aria-label="Dismiss install prompt"
        onClick={dismiss}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-3 transition-colors hover:bg-bg-2 hover:text-fg"
      >
        <X size={14} />
      </button>
    </div>
  );
}
